import {
  ProprietaMisurataDto,
  ProprietaMisurataTypeDto,
  ProprietaMisurataValueDto,
  NodoDto,
  SogliaCheckType,
} from "@/api";
import { TFunction } from "i18next";
import moment from "moment";
import numeral from "numeral";
import { orderBy } from "lodash";

export interface ProprietaMisurataTypeHelper {
  label: string;
  shortLabel?: string;
  unit: string | null;
  unitPosition: "left" | "right" | false;
  decimals: number;
  format: (value: number, skipMeasureUnit?: boolean) => string;
  getPercent: (value: number) => number;
  suggestedMin: number;
  suggestedMax: number;
  order: number;
  colorHex: string | null;
}

export function isIso(type: ProprietaMisurataTypeDto): boolean {
  return type.gruppo === "ParticelleIso";
}

export function isRms(type: ProprietaMisurataTypeDto): boolean {
  return type.gruppo === "Rms";
}

export function isFft(type: ProprietaMisurataTypeDto): boolean {
  return type.gruppo === "Fft";
}

export function isNexusPin(type: ProprietaMisurataTypeDto): boolean {
  return type.gruppo === "NexusPin";
}

export function isResistenzaTag(type: ProprietaMisurataTypeDto): boolean {
  return /^resigrp[1-6]$/.test(type.influxDbTag);
}

// -------------------------------------------------
// -------------------------------------------------
// -------------------------------------------------

export function proprietaMisurataFormat(
  { unitPosition, unit, decimals }: ProprietaMisurataTypeDto,
  value: number,
  skipMeasureUnit = false
) {
  const formatString = decimals === 0 ? "0" : `0.${"0".repeat(decimals)}`;
  const valueString = numeral(value).format(formatString);
  if (unit && !skipMeasureUnit) {
    return unitPosition === "Left"
      ? `${unit} ${valueString}`
      : `${valueString} ${unit}`;
  }
  return valueString;
}

export function proprietaMisurataGetPercent(
  { percentMin, percentMax }: ProprietaMisurataTypeDto,
  value: number
) {
  if (typeof percentMin !== "number" || typeof percentMax !== "number") {
    return null;
  }

  const rangeDelta = percentMax - percentMin;
  const MIN = 5;
  const MAX = 100;

  const delta = value - percentMin;
  let perc = (delta * 100) / rangeDelta;
  if (perc < MIN) {
    perc = MIN;
  } else if (perc > MAX) {
    perc = MAX;
  }
  return perc;
}

export function isDanger(value: ProprietaMisurataValueDto | null) {
  return typeof value?.value === "number"
    ? (typeof value.dangerMin === "number" && value.value <= value.dangerMin) ||
        (typeof value.dangerMax === "number" && value.value >= value.dangerMax)
    : false;
}

export function isOld(value: ProprietaMisurataValueDto | null) {
  if (!value) {
    return true;
  }
  const maxDiff = 2 * 24 * 60 * 60 * 1000;
  const diff = moment().diff(moment(value.timestamp));
  return diff >= maxDiff;
}

export function sortProprietaMisurate(
  proprietaMisurate: ProprietaMisurataDto[]
) {
  return orderBy(proprietaMisurate, (p) => p.type.order);
}

export function splitAndSortProprietaMisurate(
  _proprietaMisurate: ProprietaMisurataDto[]
) {
  const proprietaMisurate = sortProprietaMisurate(_proprietaMisurate);

  let proprietaStandard = proprietaMisurate.filter(
    (p) =>
      !isIso(p.type) &&
      !isRms(p.type) &&
      !isFft(p.type) &&
      !isNexusPin(p.type) &&
      !isResistenzaTag(p.type)
  );
  const proprietaISO = proprietaMisurate.filter((p) => isIso(p.type));
  let proprietaRms = proprietaMisurate.filter((p) => isRms(p.type));
  const proprietaFft = proprietaMisurate.filter((p) => isFft(p.type));

  if (proprietaRms.length < 3) {
    proprietaStandard = [...proprietaStandard, ...proprietaRms];
    proprietaRms = [];
  }

  const proprietaNexusPin = proprietaMisurate.filter((p) => isNexusPin(p.type));
  const proprietaResistenze = proprietaMisurate.filter((p) =>
    isResistenzaTag(p.type)
  );

  return {
    proprietaStandardMisurate: proprietaStandard,
    proprietaISOMisurate: proprietaISO,
    proprietaRmsMisurate: proprietaRms,
    proprietaFftMisurate: proprietaFft,
    proprietaNexusPinMisurate: proprietaNexusPin,
    proprietaResistenzeMisurate: proprietaResistenze,
  };
}

export function classeDiContaminazione(
  proprietaMisurate: ProprietaMisurataDto[]
): string | null {
  // https://www.ufihyd.com/it/blog/capire-i-materiali-filtranti-e-le-classi-di-contaminazione-nei-sistemi-oleodinamici/#:~:text=La%20classe%20di%20contaminazione%20di,4µm%2C%206µm%2C%2014µm).
  const iso = proprietaMisurate.filter(
    (x) => x.type.gruppo === "ParticelleIso"
  );
  const iso4 = iso.find((p) => p.type.influxDbTag.endsWith("4"))?.lastValue
    ?.value;
  const iso6 = iso.find((p) => p.type.influxDbTag.endsWith("6"))?.lastValue
    ?.value;
  const iso14 = iso.find((p) => p.type.influxDbTag.endsWith("14"))?.lastValue
    ?.value;

  if (iso4 && iso6 && iso14) {
    return [iso4, iso6, iso14].join("/");
  }
  return null;
}

type LabelAndSogliaType = {
  label: string;
  sogliaType: SogliaCheckType;
};

export function getAlertLabelAndSogliaType(
  dispositivo: NodoDto,
  proprietaMisurata: ProprietaMisurataDto,
  t: TFunction
): LabelAndSogliaType[] {
  if (dispositivo.tipo === "Dispositivo_Sensore") {
    return [
      { label: t("alertLabels.soglia1"), sogliaType: SogliaCheckType.moreThan },
      { label: t("alertLabels.soglia2"), sogliaType: SogliaCheckType.moreThan },
      { label: t("alertLabels.soglia3"), sogliaType: SogliaCheckType.moreThan },
      { label: t("alertLabels.soglia4"), sogliaType: SogliaCheckType.moreThan },
    ];
  }

  return [
    { label: t("alertLabels.valoreMinimo"), sogliaType: SogliaCheckType.lessThan },
    { label: t("alertLabels.valoreMassimo"), sogliaType: SogliaCheckType.moreThan },
  ];
}
